/**
 * Permissions abstraction layer
 * Centralized permission checking
 *
 * BACKEND DEPENDENCY: Roles and permissions are scoped per-workspace via Membership.
 * When the API provides explicit permissions for a membership, they take precedence
 * over the default role-based permission map below.
 */

export type Role = 'OWNER' | 'ADMIN' | 'MANAGER' | 'MEMBER' | 'VIEWER';

export type Permission =
  | 'crm.contacts.read'
  | 'crm.contacts.create'
  | 'crm.contacts.update'
  | 'crm.contacts.delete'
  | 'crm.deals.read'
  | 'crm.deals.create'
  | 'projects.projects.read'
  | 'projects.projects.create'
  | 'projects.projects.update'
  | 'projects.projects.delete'
  | 'people.employees.read'
  | 'people.employees.create'
  | 'docs.documents.read'
  | 'docs.documents.create'
  | 'docs.documents.update'
  | 'docs.documents.delete'
  | 'workspace.read'
  | 'workspace.update'
  | 'workspace.manage_members'
  | 'workspace.manage_settings'
  | 'workspace.manage_apps'
  | 'workspace.manage_roles'
  | 'admin.system'
  | 'admin.users'
  | 'admin.roles'
  | 'admin.billing';

export interface MembershipPermissions {
  role: Role;
  permissions: Permission[];
}

export interface WorkspaceAccessScope {
  workspaceId: string | number;
  membership: MembershipPermissions | null;
}

/**
 * Default role to permission mapping.
 *
 * IMPORTANT: If the backend returns explicit permissions for a membership,
 * those take priority. This map is the frontend fallback for UX gating only.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  OWNER: [
    'crm.contacts.read',
    'crm.contacts.create',
    'crm.contacts.update',
    'crm.contacts.delete',
    'crm.deals.read',
    'crm.deals.create',
    'projects.projects.read',
    'projects.projects.create',
    'projects.projects.update',
    'projects.projects.delete',
    'people.employees.read',
    'people.employees.create',
    'docs.documents.read',
    'docs.documents.create',
    'docs.documents.update',
    'docs.documents.delete',
    'workspace.read',
    'workspace.update',
    'workspace.manage_members',
    'workspace.manage_settings',
    'workspace.manage_apps',
    'workspace.manage_roles',
    'admin.system',
    'admin.users',
    'admin.roles',
    'admin.billing',
  ],
  ADMIN: [
    'crm.contacts.read',
    'crm.contacts.create',
    'crm.contacts.update',
    'crm.contacts.delete',
    'crm.deals.read',
    'crm.deals.create',
    'projects.projects.read',
    'projects.projects.create',
    'projects.projects.update',
    'projects.projects.delete',
    'people.employees.read',
    'docs.documents.read',
    'docs.documents.create',
    'docs.documents.update',
    'workspace.read',
    'workspace.update',
    'workspace.manage_members',
    'workspace.manage_settings',
    'workspace.manage_roles',
    'admin.users',
    'admin.roles',
  ],
  MANAGER: [
    'crm.contacts.read',
    'crm.contacts.create',
    'crm.contacts.update',
    'projects.projects.read',
    'projects.projects.create',
    'projects.projects.update',
    'docs.documents.read',
    'docs.documents.create',
    'workspace.read',
  ],
  MEMBER: [
    'crm.contacts.read',
    'projects.projects.read',
    'docs.documents.read',
    'workspace.read',
  ],
  VIEWER: [
    'crm.contacts.read',
    'projects.projects.read',
    'docs.documents.read',
    'workspace.read',
  ],
};

/**
 * Resolve the effective permission set for a membership.
 * API-provided permissions override the role-based defaults.
 */
export function resolvePermissions(membership: MembershipPermissions | null): Permission[] {
  if (!membership) {
    return [];
  }

  if (membership.permissions && membership.permissions.length > 0) {
    return membership.permissions;
  }

  return ROLE_PERMISSIONS[membership.role] || [];
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(
  userPermissions: Permission[] | undefined,
  userRole: Role | undefined,
  permission: Permission,
): boolean {
  if (userPermissions?.includes(permission)) {
    return true;
  }

  if (userRole) {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
  }

  return false;
}

/**
 * Check if user has any of the given permissions
 */
export function hasAnyPermission(
  userPermissions: Permission[] | undefined,
  userRole: Role | undefined,
  permissions: Permission[],
): boolean {
  return permissions.some((p) => hasPermission(userPermissions, userRole, p));
}

/**
 * Check if user has all of the given permissions
 */
export function hasAllPermissions(
  userPermissions: Permission[] | undefined,
  userRole: Role | undefined,
  permissions: Permission[],
): boolean {
  return permissions.every((p) => hasPermission(userPermissions, userRole, p));
}

/**
 * Get permissions for a role
 */
export function getRolePermissions(role: Role): Permission[] {
  return ROLE_PERMISSIONS[role] || [];
}

/**
 * Check if user is admin or owner
 */
export function isAdminOrOwner(role: Role | undefined): boolean {
  return role === 'OWNER' || role === 'ADMIN';
}

/**
 * Check if user is owner
 */
export function isOwner(role: Role | undefined): boolean {
  return role === 'OWNER';
}

/**
 * Check if user is admin
 */
export function isAdmin(role: Role | undefined): boolean {
  return role === 'ADMIN';
}

/**
 * Check if user is manager
 */
export function isManager(role: Role | undefined): boolean {
  return role === 'MANAGER';
}

/**
 * Check if user is member
 */
export function isMember(role: Role | undefined): boolean {
  return role === 'MEMBER';
}

/**
 * Check if user is viewer (read-only)
 */
export function isViewer(role: Role | undefined): boolean {
  return role === 'VIEWER';
}

/**
 * Role abstraction: check if the given role matches a target role
 */
export function hasRole(targetRole: Role, currentRole: Role | undefined): boolean {
  return currentRole === targetRole;
}

/**
 * Role abstraction: check if user has any of the specified roles
 */
export function hasAnyRole(targetRoles: Role[], currentRole: Role | undefined): boolean {
  if (!currentRole) return false;
  return targetRoles.includes(currentRole);
}

/**
 * Normalize a raw role string from the API/user to the canonical Role type.
 */
export function normalizeRole(raw: string | undefined | null): Role {
  if (!raw) return 'VIEWER';
  const upper = raw.toUpperCase().trim();
  const roleMap: Record<string, Role> = {
    OWNER: 'OWNER',
    ADMIN: 'ADMIN',
    ADMINISTRATOR: 'ADMIN',
    MANAGER: 'MANAGER',
    MEMBER: 'MEMBER',
    USER: 'MEMBER',
    VIEWER: 'VIEWER',
    READONLY: 'VIEWER',
  };
  return roleMap[upper] ?? 'VIEWER';
}
